import React from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import FormattedNumberInput from "./FormattedNumberInput";
import { formatNumber } from "../utils/formatCurrency";
import { getTranslation } from "../i18n/translations";
import { trData } from "../data/dataI18n";

/**
 * Kategoriye özel akıllı hesaplama tablosu paneli.
 * Alan değerleri item.calc_values içinde saklanır (teklifle birlikte kaydedilir).
 * Hesap sonucu "Miktara Uygula" ile kalemin miktar/birimine aktarılır;
 * litre/torba gibi ara çıktılar da tek tıkla uygulanabilir.
 */
const CalcTablePanel = ({ item, itemType, index, calcDef, onPatch, lang = 'tr' }) => {
  const t = (key) => getTranslation(lang, key);
  const stored = item.calc_values || {};
  const values = calcDef.fields.reduce(
    (acc, f) => ({ ...acc, [f.key]: stored[f.key] !== undefined ? stored[f.key] : f.def }),
    {}
  );
  const result = calcDef.compute(values);

  const setVal = (key, val) => {
    onPatch({ calc_values: { ...stored, [key]: val } });
  };

  const applyQuantity = (qty, unit) => {
    const localizedUnit = trData(lang, unit);
    onPatch({ quantity: qty, unit: localizedUnit });
    toast.success(`${t('qtyApplied')}: ${formatNumber(qty)} ${localizedUnit}`);
  };

  return (
    <div
      className="rounded-xl border border-teal-200 bg-teal-50/60 p-3 space-y-3"
      data-testid={`${itemType}-calc-panel-${index}`}
    >
      <p className="text-sm font-semibold text-teal-800 flex items-center gap-1.5">
        <Calculator className="w-4 h-4" />
        {calcDef.title ? trData(lang, calcDef.title) : ""}
      </p>

      {/* Giriş alanları */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        {calcDef.fields.map((f) =>
          f.type === "bool" ? (
            <label
              key={f.key}
              className="flex items-center gap-2 text-xs text-slate-700 mt-5 cursor-pointer select-none"
            >
              <Checkbox
                checked={!!values[f.key]}
                onCheckedChange={(c) => setVal(f.key, !!c)}
                data-testid={`${itemType}-calc-field-${f.key}-${index}`}
              />
              {trData(lang, f.label)}
            </label>
          ) : (
            <div key={f.key}>
              <Label className="text-[11px] text-slate-600">{trData(lang, f.label)}</Label>
              <FormattedNumberInput
                value={values[f.key] || 0}
                onChange={(n) => setVal(f.key, n)}
                className="rounded-lg bg-white h-8 text-sm"
                data-testid={`${itemType}-calc-field-${f.key}-${index}`}
              />
            </div>
          )
        )}
      </div>

      {/* Canlı sonuçlar */}
      {result ? (
        <div className="space-y-1.5">
          {result.outputs.map((o, i) => (
            <div
              key={i}
              className="flex items-center justify-between text-xs bg-white rounded-lg border border-teal-100 px-2.5 py-1.5"
              data-testid={`${itemType}-calc-output-${i}-${index}`}
            >
              <span className="text-slate-600">{trData(lang, o.label)}</span>
              <span className="flex items-center gap-2">
                <b className="text-teal-800">
                  {formatNumber(o.value)} {trData(lang, o.unit)}
                </b>
                {o.apply && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => applyQuantity(o.value, o.unit)}
                    className="h-6 px-2 text-[10px] rounded-md border-teal-300 text-teal-700 hover:bg-teal-50"
                    data-testid={`${itemType}-calc-apply-output-${i}-${index}`}
                  >
                    {t('applyBtn')}
                  </Button>
                )}
              </span>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1">
            <span className="text-[10px] text-slate-400 italic pr-2">{trData(lang, result.note)}</span>
            <Button
              type="button"
              size="sm"
              onClick={() => applyQuantity(result.quantity, result.unit)}
              className="h-7 px-3 text-xs bg-teal-600 hover:bg-teal-700 text-white rounded-md shrink-0"
              data-testid={`${itemType}-calc-apply-${index}`}
            >
              {t('applyToQuantity')} ({formatNumber(result.quantity)} {trData(lang, result.unit)})
            </Button>
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-500 italic">
          {t('calcEnterHint')}
        </p>
      )}
    </div>
  );
};

export default CalcTablePanel;
